import { getIdFromObj, getIriFromObj } from 'utils/processors';

export const WORKMGMT_ENTITY_TYPE_FETCH_LIMIT = 1000;
export const WORKMGMT_ENTITY_TYPE_MAX_DEPTH = 3;

const ENTITY_TYPE_IRI_PATTERNS = {
    businessOpportunityType: /\/workmgmt\/businessopportunitytypes\/(\d+)$/,
    workOrderType: /\/workmgmt\/workordertypes\/(\d+)$/
};

const getEntityTypeIdFromIri = (value, nodePrefix = 'entityType') => {
    if (typeof value !== 'string') {
        return null;
    }

    const pattern = ENTITY_TYPE_IRI_PATTERNS?.[nodePrefix];
    if (!pattern) {
        return null;
    }

    const match = value.match(pattern);
    return match ? match[1] : null;
};

const normalizeText = (value) => `${value ?? ''}`.trim().toLowerCase();

const getEntityLookupKey = (entity, fieldName) => {
    if (typeof entity?.[fieldName] !== 'string') {
        return null;
    }

    const normalizedValue = entity[fieldName].trim().toLowerCase();
    return normalizedValue ? `${fieldName}:${normalizedValue}` : null;
};

const buildUniqueLookup = (metas = [], fieldName = '') => {
    const keyCounts = new Map();

    metas.forEach((meta) => {
        const lookupKey = getEntityLookupKey(meta?.entity, fieldName);

        if (lookupKey) {
            keyCounts.set(lookupKey, (keyCounts.get(lookupKey) || 0) + 1);
        }
    });

    return metas.reduce((lookup, meta) => {
        const lookupKey = getEntityLookupKey(meta?.entity, fieldName);

        if (lookupKey && keyCounts.get(lookupKey) === 1) {
            lookup.set(lookupKey, meta);
        }

        return lookup;
    }, new Map());
};

export const getEntityTypeNodeId = (entity, nodePrefix = 'entityType', fallbackIndex = 0) => {
    if (typeof entity?.treeNodeId === 'string' && entity.treeNodeId.trim()) {
        return entity.treeNodeId;
    }

    const iri = getIriFromObj(entity);
    const idFromIri = getEntityTypeIdFromIri(iri, nodePrefix);

    if (idFromIri !== null) {
        return `${nodePrefix}:${idFromIri}`;
    }

    if (iri) {
        return iri;
    }

    const id = getIdFromObj(entity);
    if (id !== undefined && id !== null && `${id}`.trim()) {
        const normalizedId = `${id}`.trim();
        const idFromStringIri = getEntityTypeIdFromIri(normalizedId, nodePrefix);

        return `${nodePrefix}:${idFromStringIri !== null ? idFromStringIri : normalizedId}`;
    }

    if (typeof entity?.acronym === 'string' && entity.acronym.trim()) {
        return `${nodePrefix}:acronym:${entity.acronym.trim().toLowerCase()}`;
    }

    if (typeof entity?.name === 'string' && entity.name.trim()) {
        return `${nodePrefix}:name:${entity.name.trim().toLowerCase()}:${fallbackIndex}`;
    }

    return `${nodePrefix}:index:${fallbackIndex}`;
};

export const getEntityTypeParentRef = (entity, nodePrefix = 'entityType') => {
    if (typeof entity?.parent?.treeNodeId === 'string' && entity.parent.treeNodeId.trim()) {
        return entity.parent.treeNodeId;
    }

    const directParentIdFromIri = getEntityTypeIdFromIri(entity?.parent, nodePrefix);
    if (directParentIdFromIri !== null) {
        return `${nodePrefix}:${directParentIdFromIri}`;
    }

    const directParentId = getIdFromObj(entity?.parent);
    if (directParentId !== undefined && directParentId !== null && `${directParentId}`.trim()) {
        const normalizedParentId = `${directParentId}`.trim();
        const parentIdFromStringIri = getEntityTypeIdFromIri(normalizedParentId, nodePrefix);

        return `${nodePrefix}:${parentIdFromStringIri !== null ? parentIdFromStringIri : normalizedParentId}`;
    }

    const directParentIri = getIriFromObj(entity?.parent);
    const directParentIdFromObjectIri = getEntityTypeIdFromIri(directParentIri, nodePrefix);
    if (directParentIdFromObjectIri !== null) {
        return `${nodePrefix}:${directParentIdFromObjectIri}`;
    }
    if (directParentIri) {
        return directParentIri;
    }

    if (typeof entity?.parent === 'number') {
        return `${nodePrefix}:${entity.parent}`;
    }

    if (typeof entity?.parent === 'string' && entity.parent.trim()) {
        return entity.parent.trim();
    }

    return null;
};

const getEntityLabel = (entity) => entity?.name || entity?.description || entity?.acronym || `${entity?.id ?? ''}`.trim();

const compareSiblingEntities = (metaA, metaB) => {
    const levelDiff = (metaA?.entity?.level ?? 0) - (metaB?.entity?.level ?? 0);
    if (levelDiff !== 0) {
        return levelDiff;
    }

    const leftDiff = Number(metaA?.entity?.left ?? 0) - Number(metaB?.entity?.left ?? 0);
    if (leftDiff !== 0) {
        return leftDiff;
    }

    const labelCompare = getEntityLabel(metaA?.entity).localeCompare(getEntityLabel(metaB?.entity));
    if (labelCompare !== 0) {
        return labelCompare;
    }

    return metaA.flatIndex - metaB.flatIndex;
};

const buildEntityTypeMetadata = (entities = [], nodePrefix = 'entityType') => {
    const sourceEntities = Array.isArray(entities) ? entities.filter(Boolean) : [];
    const metas = sourceEntities.map((entity, flatIndex) => ({
        entity,
        flatIndex,
        nodeId: getEntityTypeNodeId(entity, nodePrefix, flatIndex),
        parentRef: getEntityTypeParentRef(entity, nodePrefix)
    }));

    const metasByNodeId = new Map(metas.map((meta) => [meta.nodeId, meta]));
    const metasByEntityRef = new Map(metas.map((meta) => [meta.entity, meta]));
    const uniqueMetasById = new Map(
        metas.map((meta) => [getIdFromObj(meta.entity), meta]).filter(([id]) => id !== undefined && id !== null)
    );
    const uniqueMetasByAcronym = buildUniqueLookup(metas, 'acronym');
    const uniqueMetasByName = buildUniqueLookup(metas, 'name');
    const childrenByParent = new Map();

    const resolveParentMeta = (meta) => {
        if (meta.parentRef && metasByNodeId.has(meta.parentRef)) {
            return metasByNodeId.get(meta.parentRef);
        }

        const parentEntity = meta?.entity?.parent;
        if (!parentEntity || typeof parentEntity !== 'object' || Array.isArray(parentEntity)) {
            return null;
        }

        const parentMetaByRef = metasByEntityRef.get(parentEntity);
        if (parentMetaByRef) {
            return parentMetaByRef;
        }

        const parentId = getIdFromObj(parentEntity);
        if (parentId !== undefined && parentId !== null && uniqueMetasById.has(parentId)) {
            return uniqueMetasById.get(parentId);
        }

        const parentAcronymKey = getEntityLookupKey(parentEntity, 'acronym');
        if (parentAcronymKey && uniqueMetasByAcronym.has(parentAcronymKey)) {
            return uniqueMetasByAcronym.get(parentAcronymKey);
        }

        const parentNameKey = getEntityLookupKey(parentEntity, 'name');
        if (parentNameKey && uniqueMetasByName.has(parentNameKey)) {
            return uniqueMetasByName.get(parentNameKey);
        }

        return null;
    };

    metas.forEach((meta) => {
        const parentMeta = resolveParentMeta(meta);
        const normalizedParentRef = parentMeta?.nodeId ?? null;
        meta.parentNodeId = normalizedParentRef;

        if (!childrenByParent.has(normalizedParentRef)) {
            childrenByParent.set(normalizedParentRef, []);
        }

        childrenByParent.get(normalizedParentRef).push(meta);
    });

    childrenByParent.forEach((siblings) => siblings.sort(compareSiblingEntities));

    return { childrenByParent };
};

export const buildEntityTypeTreeRows = (entities = [], nodePrefix = 'entityType') => {
    const { childrenByParent } = buildEntityTypeMetadata(entities, nodePrefix);
    const treeRows = [];

    const walk = (parentNodeId = null, inheritedNodePath = [], inheritedLabelPath = []) => {
        const siblings = childrenByParent.get(parentNodeId) || [];

        siblings.forEach((meta) => {
            const label = getEntityLabel(meta.entity);
            const treePath = [...inheritedNodePath, meta.nodeId];
            const treeLabelPath = [...inheritedLabelPath, label];

            treeRows.push({
                ...meta.entity,
                id: meta.entity?.id ?? meta.nodeId,
                treeNodeId: meta.nodeId,
                treePath,
                treeDepth: Math.max(treePath.length - 1, 0),
                treeLabelPath,
                treeBreadcrumbLabel: treeLabelPath.filter(Boolean).join(' / '),
                isRoot: parentNodeId === null
            });

            walk(meta.nodeId, treePath, treeLabelPath);
        });
    };

    walk(null);

    return treeRows;
};

export const buildEntityTypeHierarchy = (entities = [], nodePrefix = 'entityType') => {
    const treeRows = buildEntityTypeTreeRows(entities, nodePrefix);
    const nodesByNodeId = new Map(treeRows.map((row) => [row.treeNodeId, { ...row, children: [] }]));
    const hierarchy = [];

    treeRows.forEach((row) => {
        const parentNodeId = row.treePath?.at(-2) || null;
        const currentNode = nodesByNodeId.get(row.treeNodeId);

        if (parentNodeId && nodesByNodeId.has(parentNodeId)) {
            nodesByNodeId.get(parentNodeId).children.push(currentNode);
        } else {
            hierarchy.push(currentNode);
        }
    });

    return hierarchy;
};

const getRowFieldValue = (row, fieldName) => {
    const value = row?.[fieldName];

    if (fieldName === 'createdAt' || fieldName === 'updatedAt' || fieldName === 'deletedAt') {
        return value ? new Date(value).getTime() : null;
    }

    if (Array.isArray(value)) {
        return value.map((item) => getRowFieldValue({ value: item }, 'value')).join(' ');
    }

    if (value && typeof value === 'object') {
        return [value?.id, value?.acronym, value?.name, value?.description].filter(Boolean).join(' ');
    }

    return value;
};

const matchesOperator = (rowValue, operator, expectedValue) => {
    const valueText = normalizeText(rowValue);
    const expectedText = normalizeText(expectedValue);

    switch (operator) {
        case 'eq':
            return Array.isArray(expectedValue)
                ? expectedValue.some((value) => normalizeText(getIdFromObj(value) ?? value) === valueText)
                : valueText === expectedText;
        case 'neq':
            return Array.isArray(expectedValue)
                ? expectedValue.every((value) => normalizeText(getIdFromObj(value) ?? value) !== valueText)
                : valueText !== expectedText;
        case 'like':
            return valueText.includes(expectedText);
        case 'notLike':
            return !valueText.includes(expectedText);
        case 'isNull':
            return rowValue === null || rowValue === undefined || rowValue === '';
        case 'isNotNull':
            return rowValue !== null && rowValue !== undefined && rowValue !== '';
        case 'in':
            return Array.isArray(expectedValue)
                ? expectedValue.some((value) => normalizeText(getIdFromObj(value) ?? value) === valueText)
                : false;
        case 'notIn':
            return Array.isArray(expectedValue)
                ? expectedValue.every((value) => normalizeText(getIdFromObj(value) ?? value) !== valueText)
                : true;
        case 'lt':
            return rowValue < expectedValue;
        case 'lte':
            return rowValue <= expectedValue;
        case 'gt':
            return rowValue > expectedValue;
        case 'gte':
            return rowValue >= expectedValue;
        case 'between':
            return Array.isArray(expectedValue) && expectedValue.length === 2
                ? rowValue >= expectedValue[0] && rowValue <= expectedValue[1]
                : true;
        default:
            return true;
    }
};

const matchesFilterModel = (row, filterModel) => {
    const filterRows = filterModel?.filterRows?.filter((filterRow) => filterRow?.field && filterRow?.operator) || [];

    if (filterRows.length === 0) {
        return true;
    }

    const results = filterRows.map((filterRow) => {
        const fieldName = filterRow?.field?.fieldName || filterRow?.field;
        const rowValue = getRowFieldValue(row, fieldName);

        return matchesOperator(rowValue, filterRow?.operator, filterRow?.value);
    });

    return filterModel?.filterType === 'or' ? results.some(Boolean) : results.every(Boolean);
};

export const filterTreeRowsPreservingAncestors = (rows = [], ...models) => {
    const activeModels = models.filter((model) => model?.filterRows?.length > 0);

    if (activeModels.length === 0) {
        return rows;
    }

    const includedNodeIds = new Set();

    rows.forEach((row) => {
        const matchesAllModels = activeModels.every((model) => matchesFilterModel(row, model));

        if (matchesAllModels) {
            row?.treePath?.forEach((nodeId) => includedNodeIds.add(nodeId));
        }
    });

    return rows.filter((row) => includedNodeIds.has(row?.treeNodeId));
};

export const collectEntityTypeBranchIds = (entities = [], targetEntity = null, nodePrefix = 'entityType') => {
    const treeRows = buildEntityTypeTreeRows(entities, nodePrefix);
    const targetNodeId = getEntityTypeNodeId(targetEntity, nodePrefix);
    const targetRow = treeRows.find((row) => row.treeNodeId === targetNodeId);

    if (!targetRow) {
        return [];
    }

    return treeRows
        .filter((row) => targetRow.treePath.every((segment, index) => row.treePath[index] === segment))
        .map((row) => row.id)
        .filter((id) => id !== undefined && id !== null);
};
